import type { RightToInformationWizardDraft } from '../../../types/rightToInformationCaseRequest';
import {
  contactNumberError,
  exceedsMax,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import { validationDateFutureOnly, validationDocReasonRequired } from '../../../utils/validationI18n';
import {
  draftRequiresSubFileCategory,
  type SubFileCategoryValidationContext,
} from '../../../utils/subFileCategoryRules';
import type { RightToInformationWizardStep } from './rightToInformationConstants';
import {
  emailAddressError,
  isHighUrgencyLevel,
  minDeadlineDateForRightToInformation,
} from './rightToInformationHelpers';

const MAX_NAME = 2000;
const MAX_SHORT = 255;

export const MAX_DOC_SKIP_REASON = 255;

export function validateRightToInformationStep(
  step: RightToInformationWizardStep,
  draft: RightToInformationWizardDraft,
  options?: SubFileCategoryValidationContext,
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'rti-fileCategory' },
      { key: 'requestorName', label: 'Name of Requestor', value: draft.requestorName, elementId: 'rti-requestorName' },
      {
        key: 'nicOrPassportNumber',
        label: 'NIC/ Passport Number',
        value: draft.nicOrPassportNumber,
        elementId: 'rti-nicOrPassport',
      },
      { key: 'address', label: 'Address', value: draft.address, elementId: 'rti-address' },
      {
        key: 'isSriLankanCitizen',
        label: 'Is the requestor a citizen of Sri Lanka?',
        value: draft.isSriLankanCitizen,
        elementId: 'rti-citizen',
      },
      {
        key: 'specificPublicAuthorityName',
        label: 'Specific Public Authority Name',
        value: draft.specificPublicAuthorityName,
        elementId: 'rti-publicAuthority',
      },
      {
        key: 'requestedInformationDetails',
        label: 'Requested Information Details',
        value: draft.requestedInformationDetails,
        elementId: 'rti-requestedDetails',
      },
      {
        key: 'preferredMethodOfAccessIds',
        label: 'Preferred Method of Access',
        value: draft.preferredMethodOfAccessIds,
        elementId: 'rti-preferredAccess',
      },
      {
        key: 'preferredLanguageId',
        label: 'Preferred Language for Response',
        value: draft.preferredLanguageId,
        elementId: 'rti-preferredLanguage',
      },
      {
        key: 'involvesLifeOrPersonalLiberty',
        label: 'Does this request involve life or personal liberty of a citizen?',
        value: draft.involvesLifeOrPersonalLiberty,
        elementId: 'rti-lifeLiberty',
      },
    );

    if (draftRequiresSubFileCategory(draft, options)) {
      specs.push({
        key: 'subFileCategoryId',
        label: 'Sub File Category',
        value: draft.subFileCategoryId,
        elementId: 'rti-subFileCategory',
      });
    }

    if (draft.involvesLifeOrPersonalLiberty === 'yes') {
      specs.push({
        key: 'justification',
        label: 'Justification',
        value: draft.justification,
        elementId: 'rti-justification',
      });
    }

    if (exceedsMax(draft.requestorName, MAX_NAME)) extra.requestorName = maxCharLimitExceededError();
    if (exceedsMax(draft.nicOrPassportNumber, MAX_SHORT)) extra.nicOrPassportNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.address, MAX_NAME)) extra.address = maxCharLimitExceededError();
    if (exceedsMax(draft.emailAddress, MAX_SHORT)) extra.emailAddress = maxCharLimitExceededError();
    if (exceedsMax(draft.specificPublicAuthorityName, MAX_SHORT)) {
      extra.specificPublicAuthorityName = maxCharLimitExceededError();
    }
    if (exceedsMax(draft.requestedInformationDetails, MAX_NAME)) {
      extra.requestedInformationDetails = maxCharLimitExceededError();
    }
    if (exceedsMax(draft.informationRequestedTimePeriod, MAX_SHORT)) {
      extra.informationRequestedTimePeriod = maxCharLimitExceededError();
    }
    if (exceedsMax(draft.justification, MAX_NAME)) extra.justification = maxCharLimitExceededError();
    if (exceedsMax(draft.additionalNotes, MAX_NAME)) extra.additionalNotes = maxCharLimitExceededError();

    const contactErr = contactNumberError(draft.contactNumber);
    if (contactErr) extra.contactNumber = contactErr;
    const emailErr = emailAddressError(draft.emailAddress);
    if (emailErr) extra.emailAddress = emailErr;
  }

  if (step >= 2) {
    specs.push({
      key: 'urgencyLevelId',
      label: 'Urgency Level',
      value: draft.urgencyLevelId,
      elementId: 'rti-urgency',
    });
    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'rti-urgencyJustification',
      });
    }
    if (exceedsMax(draft.urgencyJustification, MAX_NAME)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 4) {
    Object.assign(extra, validateStep4Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep4Documents(draft: RightToInformationWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if (doc.fileName) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}

export { isHighUrgencyLevel, minDeadlineDateForRightToInformation };
